import createCache from '@emotion/cache';

function getReverseRotateTransform(transform: string): string {
  if (!transform || transform === 'none') return '';
  const match = transform.match(/^matrix\(([^)]+)\)$/);
  if (!match) return '';
  const values = match[1].split(',').map(Number);
  if (values.length < 6) return '';
  const [a, b] = values;
  const angle = Math.atan2(b, a);
  const deg = -angle * (180 / Math.PI);
  const degInt = Math.round(deg);

  if (degInt === 90) {
    return 'rotateZ(90deg) translateY(-100vw)';
  } else if (degInt === -90 || degInt === 270) {
    return 'rotateZ(-90deg) translateX(-100vh)';
  } else if (degInt === 180 || degInt === -180) {
    return 'rotateZ(180deg) translate(-100vw, -100vh)';
  } else {
    return `rotateZ(${degInt}deg)`;
  }
}

function applyReverseTransform(iframe: HTMLIFrameElement) {
  const bodyTransform = window.getComputedStyle(document.body).transform;
  let reverse = '';
  if (bodyTransform && bodyTransform !== 'none') {
    reverse = getReverseRotateTransform(bodyTransform);
  }
  iframe.style.transform = reverse;
  iframe.style.transformOrigin = '0 0';
}

function setupTransformSync(iframe: HTMLIFrameElement): () => void {
  const sync = () => applyReverseTransform(iframe);
  window.addEventListener('resize', sync);
  window.addEventListener('orientationchange', sync);
  sync(); // Apply initial transform
  return () => {
    window.removeEventListener('resize', sync);
    window.removeEventListener('orientationchange', sync);
  };
}

class SingletonIframeManager {
  private persistentIframe: HTMLIFrameElement | null = null;
  public iframeEmotionCache: ReturnType<typeof createCache> | null = null;
  public iframeLoadPromise: Promise<void> | null = null;
  private iframeLoadResolver: (() => void) | null = null;
  private removeGlobalTransformSync: (() => void) | null = null;
  private referenceCount = 0;

  constructor(private zIndex: number, private pointerEvents: string) {}

  private create() {
    if (typeof window === 'undefined') return;
    if (this.persistentIframe && document.body.contains(this.persistentIframe)) {
      return;
    }

    this.persistentIframe = document.createElement('iframe');
    this.persistentIframe.style.position = 'fixed';
    this.persistentIframe.style.left = '0';
    this.persistentIframe.style.top = '0';
    this.persistentIframe.style.width = '100vw';
    this.persistentIframe.style.height = '100vh';
    this.persistentIframe.style.zIndex = String(this.zIndex);
    this.persistentIframe.style.pointerEvents = this.pointerEvents;
    this.persistentIframe.style.border = 'none';
    this.persistentIframe.style.background = 'transparent';
    this.persistentIframe.setAttribute('allowtransparency', 'true');
    this.persistentIframe.style.display = 'none'; // Initially hidden

    document.body.appendChild(this.persistentIframe);

    this.iframeLoadPromise = new Promise<void>((resolve) => {
      this.iframeLoadResolver = resolve;
    });

    this.persistentIframe.onload = () => {
      if (!this.persistentIframe?.contentDocument?.head) {
        console.error('[SingletonIframe] Iframe loaded, but document or head is not available.');
        return;
      }
      console.log('[SingletonIframe] Iframe content loaded.');
      this.iframeEmotionCache = createCache({
        key: `iframe-css`,
        container: this.persistentIframe.contentDocument.head,
        prepend: true
      });
      if (this.removeGlobalTransformSync) this.removeGlobalTransformSync();
      this.removeGlobalTransformSync = setupTransformSync(this.persistentIframe);

      const doc = this.persistentIframe.contentDocument;
      if (doc) {
        const rootVars = ['--status-bar-height', '--navigation-bar-height'];
        rootVars.forEach((varName) => {
          const value = document.documentElement.style.getPropertyValue(varName);
          if (value) {
            doc.body.style.setProperty(varName, value);
          }
        });
      }

      this.iframeLoadResolver?.();
      this.iframeLoadResolver = null;
    };

    this.persistentIframe.onerror = () => {
      console.error('[SingletonIframe] Iframe failed to load.');
    };

    this.persistentIframe.src = 'about:blank';
  }

  public async acquire(): Promise<{
    iframe: HTMLIFrameElement;
    emotionCache: NonNullable<ReturnType<typeof createCache>>;
  }> {
    this.create(); // ensure iframe exists
    await this.iframeLoadPromise;

    if (!this.persistentIframe || !this.iframeEmotionCache) {
      throw new Error('[SingletonIframe] Iframe or cache not available after load.');
    }

    this.referenceCount++;
    if (this.persistentIframe.style.display === 'none') {
      applyReverseTransform(this.persistentIframe);
      this.persistentIframe.style.display = 'block';
    }

    return {
      iframe: this.persistentIframe,
      emotionCache: this.iframeEmotionCache
    };
  }

  public release() {
    this.referenceCount--;
    if (this.referenceCount <= 0) {
      this.referenceCount = 0;
      if (this.persistentIframe) {
        this.persistentIframe.style.display = 'none';
      }
    }
  }
}

export const modalIframeManager = new SingletonIframeManager(99999998, 'auto');
// for Tosat
export const toastIframeManager = new SingletonIframeManager(99999999, 'none');
